/**
 * Row builders for the product master export.
 *
 * Sheet 2 (套裝組成) is deliberately the same three columns the bulk import
 * reads, so a merchant can export the catalogue, edit the recipes in Excel and
 * re-import the file to update compositions. `productExport.test.ts` feeds the
 * generated sheet straight back through the import parser to pin that contract.
 *
 * Plain module, no React: the vitest config only collects src/**\/*.test.ts and
 * runs in a `node` environment.
 */

/** Sheet 1 headers — unchanged from the previous CSV export, same order. */
export const PRODUCT_HEADERS = [
  '商家參考', 'SKU ID', '內部參考', '條形碼', '產品名稱', '英文名稱', '中文名稱',
  '品牌', '描述', '產品長度(cm)', '產品寬度(cm)', '產品高度(cm)', '產品重量(kg)',
  '包裝長度(cm)', '包裝寬度(cm)', '包裝高度(cm)', '包裝重量(kg)', '包裝體積(m³)', '單位體積(m³)',
  '基本單位', '包裝單位', '轉換因子', '批次號', '序號', '批次控制策略',
  '總庫存', '最大庫存',
  '是否捆綁', '是否活躍', '創建時間', '更新時間', '供應商', '產品分類',
] as const

/** Sheet 2 headers — must match the import's 套裝組成 sheet exactly. */
export const BUNDLE_HEADERS = ['套裝SKU', '組件SKU', '每套數量'] as const

/**
 * Sheet 3 headers. Reference only — the bulk import does not read this sheet,
 * so editing it and re-importing has no effect. A different relationship from
 * 套裝組成: these are alias SKUs that share the parent product's inventory
 * (product_alternate_skus), not components a set is built from.
 */
export const ALTERNATE_HEADERS = ['主要SKU', '備用SKU'] as const

const yesNo = (v: unknown) => (v ? '是' : '否')
const day = (v: unknown) => (v ? new Date(v as any).toISOString().split('T')[0] : '')

export function buildProductRows(products: any[]): any[][] {
  return (products || []).map(item => [
    item.merchantReference || '',
    item.sku || '',
    item.internalReference || '',
    item.barcode || '',
    item.productName || '',
    item.productNameEnglish || '',
    item.productNameChinese || '',
    item.brand || '',
    item.description || '',
    item.length ?? '',
    item.width ?? '',
    item.height ?? '',
    item.weight ?? '',
    item.packagingLength ?? '',
    item.packagingWidth ?? '',
    item.packagingHeight ?? '',
    item.packagingWeight ?? '',
    item.packageCBM ?? '',
    item.unitCBM ?? '',
    item.baseUOM || '',
    item.packagingUOM || '',
    item.conversionFactor ?? '',
    item.lotNumber || '',
    item.serialNumber || '',
    item.batchControlType || '',
    item.totalStock ?? '',
    item.maxStock ?? '',
    yesNo(item.isBundle),
    yesNo(item.isActive),
    // Real dates now: xlsx keeps these as text without the zero-width hack the
    // CSV export needed to stop Excel reformatting them.
    day(item.createdAt),
    day(item.updatedAt),
    item.supplier || '',
    Array.isArray(item.category) ? item.category.join('; ') : '',
  ])
}

/**
 * One row per (bundle parent, component). Components with no resolvable SKU are
 * dropped rather than written blank — a blank 組件SKU would fail validation on
 * re-import and give the merchant nothing to act on.
 */
export function buildBundleRows(products: any[]): any[][] {
  const rows: any[][] = []
  for (const item of products || []) {
    if (!item?.isBundle) continue
    const parentSku = String(item.sku || '').trim()
    if (!parentSku) continue
    for (const component of item.bundleItems || []) {
      const componentSku = String(component?.sku || '').trim()
      if (!componentSku) continue
      const quantity = Number(component?.quantity)
      rows.push([parentSku, componentSku, Number.isFinite(quantity) && quantity > 0 ? quantity : 1])
    }
  }
  return rows
}

/**
 * One row per (parent SKU, alternate SKU) pair. A product listing itself as its
 * own alternate is dropped — the product form rejects that, but historical rows
 * predate the check and would read as a self-referencing loop in the report.
 */
export function buildAlternateRows(products: any[]): any[][] {
  const rows: any[][] = []
  for (const item of products || []) {
    const parentSku = String(item?.sku || '').trim()
    if (!parentSku) continue
    if (!Array.isArray(item?.alternateSkuList)) continue
    for (const alternate of item.alternateSkuList) {
      const alternateSku = String(alternate ?? '').trim()
      if (!alternateSku || alternateSku === parentSku) continue
      rows.push([parentSku, alternateSku])
    }
  }
  return rows
}
